/**
 * Klub uzytkownika z `MyClubResponse` (`GET /api/clubs/my`).
 *
 * Celowo nie ma tu odpowiednika `GET /api/clubs`, ktory zwraca wszystkie kluby
 * w systemie: do wyboru klubu w panelu zarzadzania wolno uzywac wylacznie listy
 * zawezonej do uzytkownika, inaczej przelacznik pokazuje cudze kluby.
 */
export interface MyClub {
  id: string;
  name: string;
  /** Adres logo klubu; `null`, gdy klub go nie ustawil. */
  logoUrl: string | null;
  city: string | null;
  /** Rola uzytkownika w tym klubie — systemowa albo zdefiniowana przez klub. */
  roleName: string;
}

/**
 * Wizytowka klubu z `GET /api/clubs/{id}` — tylko pola potrzebne widokom
 * (np. „Obiekt" na stronie wyniku meczu). Odpowiedz API ma ich wiecej.
 */
export interface ClubProfile {
  id: string;
  name: string;
  city: string | null;
}

/**
 * Fizyczny obiekt klubu (kort) z `FacilityResponse`.
 *
 * To jest „prawdziwy" kort — ten, ktory kluby wspoldziela miedzy turniejami.
 * Kort turniejowy z modulu rozgrywek tylko na niego wskazuje.
 */
export interface ClubFacility {
  id: string;
  clubId: string;
  name: string;
  description: string | null;
  /** Dyscyplina w zapisie API, np. `TENNIS`. */
  discipline: string | null;
  indoor: boolean;
  lighting: boolean;
  /** Nawierzchnia w zapisie API, np. `CLAY`. */
  surface: string | null;
  status: string | null;
}

/**
 * Klub wraz z lista obiektow — z `GET /api/clubs`.
 *
 * Uzywamy go wylacznie do przegladania infrastruktury: organizator szuka kortu,
 * na ktorym rozlozy mecze, takze poza wlasnym klubem. Do wyboru kontekstu pracy
 * sluzy `MyClub` (patrz komentarz przy `getMyClubs`).
 */
export interface ClubWithFacilities {
  id: string;
  name: string;
  city: string | null;
  street: string | null;
  facilities: ClubFacility[];
}

/**
 * Pelne dane klubu z `ClubResponse` (`GET /api/clubs`) — panel administratora.
 */
export interface AdminClub {
  id: string;
  name: string;
  description: string | null;
  logoUrl: string | null;
  city: string | null;
  street: string | null;
  postalCode: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  /** STANDARD (wlasne obiekty) albo VIRTUAL (dziala na dzierzawionych slotach). */
  clubType: 'STANDARD' | 'VIRTUAL' | null;
  facilities: ClubFacility[];
}

/** Payload tworzenia klubu (`POST /api/clubs`, tylko ADMIN). */
export interface CreateClubRequest {
  name: string;
  description: string | null;
  logoUrl: string | null;
  /** Uzytkownik, ktory zostanie pierwszym czlonkiem z rola OWNER. */
  ownerId: string;
  city: string | null;
  street: string | null;
  postalCode: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  virtualClub: boolean;
}

/** Payload edycji klubu (`PUT /api/clubs/{id}`, tylko ADMIN). */
export type UpdateClubRequest = Omit<CreateClubRequest, 'ownerId' | 'virtualClub'>;

/**
 * Klucze tlumaczen rol systemowych klubu.
 *
 * Rola jest po stronie backendu zwyklym napisem, bo kluby moga definiowac wlasne.
 * Znane role tlumaczymy, nieznane pokazujemy tak, jak nazwal je klub.
 */
const SYSTEM_CLUB_ROLE_KEYS = {
  OWNER: 'clubRoles.OWNER',
  MANAGER: 'clubRoles.MANAGER',
  TRAINER: 'clubRoles.TRAINER',
  WORKER: 'clubRoles.WORKER',
} as const;

export type SystemClubRoleKey = (typeof SYSTEM_CLUB_ROLE_KEYS)[keyof typeof SYSTEM_CLUB_ROLE_KEYS];

/** Zwraca klucz tlumaczenia roli albo `null` dla roli wlasnej klubu. */
export function clubRoleLabelKey(roleName: string): SystemClubRoleKey | null {
  return roleName in SYSTEM_CLUB_ROLE_KEYS
    ? SYSTEM_CLUB_ROLE_KEYS[roleName as keyof typeof SYSTEM_CLUB_ROLE_KEYS]
    : null;
}
